import { Trash2, User } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { XStack, YStack } from "tamagui";
import { LocationItemHotelsTipsUI } from "../../../affiliates/booking/LocationItemHotelsTipsUI";
import { useStores } from "../../../core/stores";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Box } from "../../../ui/Box";
import { ModuleEditor } from "../../../ui/editor/useModuleEditor";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Title } from "../../../ui/Title";
import {
  AdvancedRatingsInput,
  RatingsInput,
} from "../../elements/rating/RatingUI";
import LocationModel from "../LocationModel";
import type LocationModuleModel from "../LocationModuleModel";
import type { MapHandler } from "./Map";

export const LocationDescription: React.FC<{
  locationItem: LocationModel;
}> = observer(({ locationItem }) => {
  return <ModuleEditor module={locationItem} />;
});

export const Location: React.FC<{
  map: MapHandler;
  module: LocationModuleModel;
  dataUid: string;
}> = observer(({ map, module, dataUid }) => {
  const { modulesDataStore } = useStores();
  const dataItem = modulesDataStore.byId.get(dataUid);
  const locationItem =
    dataItem && LocationModel.is(dataItem) ? dataItem : undefined;
  const { replace } = usePlanNavigation();

  useEffect(() => {
    const mapData = locationItem?.data.mapData;
    if (mapData) {
      setTimeout(() => {
        map.map.setZoom(9);
        map.map.setCenter({ lat: mapData.lat, lng: mapData.lng });
      }, 250);
    } else {
      map.map.setZoom(1);
    }
  }, [locationItem, map]);

  const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
    if (!locationItem?.user) {
      return [];
    }

    return [
      {
        icon: User,
        text: `Added by ${locationItem.user.data.displayName}`,
      },
    ];
  }, [locationItem?.user]);

  if (!locationItem) {
    return null;
  }

  const handleDelete = () => {
    locationItem.deleteData(locationItem.data.label);
    replace("Module", {
      planUid: locationItem.module.plan.uid,
      moduleUid: locationItem.data.moduleUid,
    });
  };

  return (
    <YStack gap="$medium">
      <Box>
        <RatingsInput
          ratings={module.ratings}
          dataUid={dataUid}
          disabled={module.isDone}
          showHint
        />
      </Box>
      <Box>
        <XStack alignItems="center" justifyContent="space-between">
          <Title>{locationItem.data.label}</Title>
          {module.isEditable && (
            <PopoverConfirm onConfirm={handleDelete}>
              <Trash2 size={"$size.normal"} color={"$muted"} />
            </PopoverConfirm>
          )}
        </XStack>
        <LocationDescription
          key={locationItem.uid}
          locationItem={locationItem}
        />
        <InfoRow items={infoRowItems} />
      </Box>

      <Box>
        <AdvancedRatingsInput ratings={module.ratings} dataUid={dataUid} />
      </Box>

      {dataItem && (
        <Box noPadding>
          <LocationItemHotelsTipsUI locationItem={locationItem} />
        </Box>
      )}
    </YStack>
  );
});
